import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const ConfirmEndDialog = ({
  open,
  loading,
  error,
  onConfirm,
  onCancel,

  // — customisable copy —
  title        = "End Allocation",
  subtitle     = "This action cannot be undone",
  confirmLabel = "End Allocation",
  warningText  = "Are you sure you want to end this allocation?",

  // — info card lines —
  primaryText,    // bold top line   e.g. associate name / position code
  badgeText,      // mono accent line e.g. position code / type badge
  subText,        // small grey line  e.g. "Role · SOW"
}) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={loading ? undefined : onCancel}
        sx={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 1300,
          cursor: loading ? "not-allowed" : "default",
        }}
      />

      {/* Dialog */}
      <Box sx={{
        position: "fixed",
        top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1301,
        width: "380px",
        background: t.surface,
        border: `1px solid ${t.border2}`,
        borderLeft: "3px solid #c84040",
        borderRadius: "6px",
        overflow: "hidden",
        boxShadow: isDark
          ? "0 8px 32px rgba(0,0,0,0.5)"
          : "0 8px 32px rgba(0,0,0,0.15)",
      }}>

        {/* Header */}
        <Box sx={{
          padding: "16px 20px 12px",
          borderBottom: `1px solid ${t.border}`,
        }}>
          <Typography sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "14px", fontWeight: 700, color: t.text, mb: "2px",
          }}>
            {title}
          </Typography>
          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "9px", color: t.text3, letterSpacing: "0.04em",
          }}>
            {subtitle}
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ padding: "16px 20px" }}>

          {/* Info card */}
          <Box sx={{
            padding: "10px 14px",
            background: t.surface2,
            border: `1px solid ${t.border2}`,
            borderRadius: "4px", mb: "14px",
          }}>
            {primaryText && (
              <Typography sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "13px", fontWeight: 600, color: t.text, mb: "2px",
              }}>
                {primaryText}
              </Typography>
            )}
            {badgeText && (
              <Typography sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "9px", color: t.accent, mb: "1px",
              }}>
                {badgeText}
              </Typography>
            )}
            {subText && (
              <Typography sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "11px", color: t.text3,
              }}>
                {subText}
              </Typography>
            )}
          </Box>

          {/* Warning */}
          <Typography sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "12px", color: t.text2, lineHeight: 1.6,
          }}>
            {warningText}
          </Typography>
        </Box>

        {/* Error */}
        {error && (
          <Box sx={{ padding: "0 20px 12px" }}>
            <Box sx={{
              padding: "8px 12px",
              background: "rgba(200,64,64,0.08)",
              border: "1px solid rgba(200,64,64,0.25)",
              borderRadius: "3px",
            }}>
              <Typography sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "10px", color: "#c84040", letterSpacing: "0.04em",
              }}>
                {error}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{
          display: "flex", justifyContent: "flex-end", gap: "8px",
          padding: "12px 20px",
          borderTop: `1px solid ${t.border}`,
        }}>
          <Box
            onClick={loading ? undefined : onCancel}
            sx={{
              padding: "7px 18px", borderRadius: "3px",
              cursor: loading ? "not-allowed" : "pointer",
              border: `1px solid ${t.border2}`,
              fontFamily: "'DM Mono', monospace", fontSize: "11px",
              color: loading ? t.text3 : t.text2,
              transition: "all 0.15s",
              "&:hover": loading ? {} : { borderColor: t.border3, color: t.text },
            }}
          >
            Cancel
          </Box>

          <Box
            onClick={loading ? undefined : onConfirm}
            sx={{
              padding: "7px 18px", borderRadius: "3px",
              cursor: loading ? "not-allowed" : "pointer",
              background: "rgba(200,64,64,0.12)",
              border: "1px solid rgba(200,64,64,0.35)",
              fontFamily: "'DM Mono', monospace", fontSize: "11px",
              fontWeight: 600,
              color: loading ? "rgba(200,64,64,0.4)" : "#c84040",
              display: "flex", alignItems: "center", gap: "6px",
              transition: "all 0.15s",
              "&:hover": loading ? {} : { background: "rgba(200,64,64,0.2)" },
            }}
          >
            {loading && (
              <Box sx={{
                width: "10px", height: "10px", borderRadius: "50%",
                border: "1.5px solid rgba(200,64,64,0.3)",
                borderTopColor: "#c84040",
                animation: "spin 0.7s linear infinite",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
              }} />
            )}
            {loading ? "Ending..." : confirmLabel}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ConfirmEndDialog;